import { isEmpty } from 'lodash';
import z from 'zod';

/** this is a helper function to make a schema optional and transform empty strings to undefined.
 *
 * This is solves the problem when sending empty strings for optional field to the backend.
 *
 * It will consider it as undefined and not send it to the backend.
 */
export const optionalStringSchema = (schema: z.ZodSchema<string>) => {
  return z.coerce
    .string()
    .optional()
    .superRefine((val, ctx) => {
      const hasValue = !isEmpty(val);
      if (hasValue) {
        const validation = schema.safeParse(val);
        if (!validation.success) {
          ctx.addIssue(validation.error.issues[0]);
        }
      }
    })
    .transform(val => (isEmpty(val) ? undefined : val));
};

/**
 * Make a schema optional and treat a blank string as "not supplied",
 * **keeping the inner schema's own transform**.
 *
 * Use this instead of {@link optionalStringSchema} whenever the inner
 * schema normalizes its value rather than only validating it — e.g.
 * `phoneSchema`, which turns `"055 899 2922"` into `"966558992922"`.
 * `optionalStringSchema` runs the inner schema through `safeParse` purely
 * to collect issues and then returns the caller's raw string, so a phone
 * would reach the database in whatever shape it was typed and quietly
 * miss every `(phone, org)` dedup lookup.
 *
 * Forms SHOULD still map blanks to `undefined` themselves; the blank
 * handling here is the schema-level safety net for the `''` that
 * controlled inputs send.
 */
export const optionalSparseSchema = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    val => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    schema.optional(),
  );
